/**
 * PAJ - Creating a Global Shared State using Context API
 * state defined in this AppContextProvider will be used throughout the context of our application
 */
import { FunctionComponent, useEffect, useState, useReducer } from 'react';
import { IAppProps } from '../interface/IAppProvider';
import { appContextReducer } from '../contextReducer/AppContextReducer';
import { AppStateContext, AppDispatchContext } from './AppContext';
import initialState from '../initialState/initialState';
import { removeSessionItem } from '../services/StorageService';
import { ROUTE_NAMES } from '../../router/RouterApp';
import { reloadApplicationStateFromCache } from '../actions/ObjectCacheActions';
import track from 'c9-tracking';
import { pathNameMatch } from '../utils/helpers/GenericHelpers';

export const AppContextProvider: FunctionComponent<IAppProps> = (props) => {
  const [state, dispatch] = useReducer(appContextReducer, initialState);
  const [refresh, setRefresh] = useState(false);
  const [trackReady, setTrackReady] = useState(false);
  const [backButtonClicked, setBackButtonClicked] = useState(false);
  const isReceipt = props.config.baseName.indexOf('kvittering') !== -1;

  //Browser back and forward
  window.onpopstate = () => {
    setBackButtonClicked(true);
  };

  useEffect(() => {
    const pathName = window.location.pathname;
    //const salesCompleteKey = getSessionItem('onsa-travel-sales-complete');
    if (!trackReady) {
      setTrackReady(true);
      track.ready(true); //Initialize Track library for sas360 tracking
    }

    function handleFlowComplete() {
      removeSessionItem('onsa-travel-session-key');
      dispatch({ emailSent: false, salesComplete: false, initialState: true });
      const baseUrl = window.location.origin;
      //Use replace to clear the history
      window.location.replace(`${baseUrl}/forsikringer/rejseforsikring/`);
    }

    //Handle refresh
    if (!state.refreshFromCache && !refresh) {
      setRefresh(true);
      //Check user route to / or intro page
      if (
        !isReceipt &&
        (pathNameMatch(pathName, props.config.baseName + ROUTE_NAMES.HOUSEHOLD) || pathName === props.config.baseName)
      ) {
        removeSessionItem('onsa-travel-session-key');
      }
      reloadApplicationStateFromCache(props, dispatch);
    }

    //back button with filled data
    if (backButtonClicked && state.prevLocation && state.hasError.stepOne) {
      handleFlowComplete();
    } else if (
      backButtonClicked &&
      (pathNameMatch(pathName, props.config.baseName + ROUTE_NAMES.HOUSEHOLD) || pathName === props.config.baseName)
    ) {
      setBackButtonClicked(false);
      removeSessionItem('onsa-travel-session-key');
      dispatch({
        refreshFromCache: true
      });
    } else {
      setBackButtonClicked(false);
    }

    //Check onsa-travel complete one flow RMO or sales complete
    if (
      (state.emailSent && pathNameMatch(pathName, props.config.baseName + ROUTE_NAMES.CALL_ME_RECEIPT)) ||
      (state.salesComplete && !isReceipt)
    ) {
      handleFlowComplete();
    }
  }, [
    props,
    refresh,
    dispatch,
    state.refreshFromCache,
    state.emailSent,
    state.salesComplete,
    backButtonClicked,
    state.initialState,
    trackReady,
    state.hasError.stepOne,
    state.prevLocation,
    isReceipt
  ]);

  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>{props.children}</AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
};

export const aliasToContentBlock = (alias: string): string => {
  let text;
  switch (alias) {
    case 'TRAVEL_WORLD':
      text = 'onsa-daekker-resten-af-verden';
      break;
    case 'CANCELLATION':
      text = 'onsa-afbestilling';
      break;
    case 'TRAVEL_EUROPE':
      text = 'onsa-daekker-i-europa';
      break;
    case 'SICKNESS_INJURY':
      text = 'onsa-sygdom-og-tilskadekomst';
      break;
    case 'TRAVEL_DELAY':
      text = 'onsa-forsinkelse-pa-rejsen';
      break;
    case 'LUGGAGE_DELAY':
      text = 'onsa-forsinket-bagage';
      break;
    case 'RUINED_DAY':
      text = 'onsa-odelagte-feriedogn';
      break;
    case 'DEDUCTIBLE_RENTAL_CAR':
      text = 'onsa-selvrisiko-ved-lejebil';
      break;
    default:
      text = '';
      break;
  }
  return text;
};
